import {
  Button,
  Checkbox,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import React, { useEffect, useMemo, useState } from 'react';

import { error as commonError } from '@finley-technologies/common';
import { DataPipeline } from '@finley-technologies/finley-types';

import { isNeededInput } from './neededInput';
import { UploadModal } from './UploadModal';
import { useNeededInput } from 'packages/dataPipeline/hooks/neededInputContext';
import { useUploadContext } from 'packages/dataPipeline/hooks/UploadContext';
import { useCreatePipelineSubmission } from 'packages/dataPipeline/hooks/useDirectUpload';
import { useGetPipelineRunInputsQuery } from 'packages/dataPipeline/requests/dataPipeline.gql.generated';

export type RunAgainVersion = { fileVersion: number; started: boolean };

/**
 * Starts the date's next version from this run's files: each file this version
 * received successfully is kept unless unchecked, and the unchecked ones are left
 * to upload in the new version. The input the caller needs starts unchecked, so
 * running again replaces just that file. With every file unchecked the new
 * version is not started: the first upload starts it. Renders nothing when there
 * is no file to keep, or when the run's mesh is not on the latest graph.
 */
export const RunAgain = ({
  onRunAgain,
}: {
  // hears the version the files were kept into, or that is yet to start
  onRunAgain: (version: RunAgainVersion) => void;
}): React.ReactElement | null => {
  const {
    meshDocument,
    meshRunParams,
    canUpload,
    completeRecord,
  } = useUploadContext();
  const { implementationId } = meshDocument;
  const { reportingDate, fileVersion } = meshRunParams.rdfv;

  const fileInputs = useMemo(
    () =>
      meshDocument.meshInputs.filter(
        (meshInput): meshInput is DataPipeline.MeshInputFile =>
          meshInput.type === 'file',
      ),
    [meshDocument.meshInputs],
  );
  const artifactOf = useMemo(() => {
    const artifacts = new Map(
      meshDocument.artifacts.map((artifact) => [artifact.id, artifact]),
    );
    return (
      meshInput: DataPipeline.MeshInputFile,
    ): DataPipeline.Artifact | null =>
      artifacts.get(meshInput.artifactId) ?? null;
  }, [meshDocument.artifacts]);
  const slugOf = useMemo(
    () => (meshInput: DataPipeline.MeshInputFile): string | null =>
      artifactOf(meshInput)?.artifactSlug ?? null,
    [artifactOf],
  );
  const neededInput = useNeededInput();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const { data, refetch } = useGetPipelineRunInputsQuery({
    variables: {
      implementationId,
      reportingDate,
      artifactSlugs: fileInputs.flatMap((meshInput) => slugOf(meshInput) ?? []),
    },
    fetchPolicy: 'network-only',
    skip: !canUpload,
  });
  // re-read the files as the run moves on, and the latest version on opening,
  // since others may have started one since
  const runUpdatedAt =
    completeRecord?.meshRunRecord.updatedAt.getTime() ?? null;
  useEffect(() => {
    if (canUpload && (isOpen || runUpdatedAt !== null)) {
      refetch();
    }
  }, [canUpload, isOpen, refetch, runUpdatedAt]);
  const runs = data?.getPipelineRunInputs ?? null;

  // this version's files that can be kept, as the drawer names them
  const keepable = useMemo(() => {
    const received = new Set(
      (runs?.find((run) => run.fileVersion === fileVersion)?.inputs ?? [])
        .filter(({ status }) => status === 'succeeded')
        .map(({ artifactSlug }) => artifactSlug),
    );
    return fileInputs.flatMap((meshInput) => {
      const artifactSlug = slugOf(meshInput);
      return artifactSlug && received.has(artifactSlug)
        ? [{ artifactSlug, name: meshInput.friendlyName }]
        : [];
    });
  }, [fileInputs, fileVersion, runs, slugOf]);
  const nextFileVersion = runs
    ? Math.max(0, ...runs.map((run) => run.fileVersion)) + 1
    : null;

  const [unchecked, setUnchecked] = useState<ReadonlySet<string>>(
    () =>
      new Set(
        neededInput === null
          ? []
          : fileInputs.flatMap((meshInput) => {
              const artifact = artifactOf(meshInput);
              return artifact &&
                isNeededInput({ neededInput, meshInput, artifact })
                ? [artifact.artifactSlug]
                : [];
            }),
      ),
  );
  const [starting, setStarting] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const createPipelineSubmission = useCreatePipelineSubmission();

  if (!canUpload || keepable.length === 0 || nextFileVersion === null) {
    return null;
  }
  const kept = keepable.filter(
    ({ artifactSlug }) => !unchecked.has(artifactSlug),
  );

  const start = async (): Promise<void> => {
    // a run needs an input: leave it to the first upload
    if (kept.length === 0) {
      onClose();
      onRunAgain({ fileVersion: nextFileVersion, started: false });
      return;
    }
    setStarting(true);
    setErrorMessage(null);
    try {
      const submission = await createPipelineSubmission({
        implementationId,
        reportingDate,
        fileVersion: nextFileVersion,
        newRun: true,
        inputs: kept.map(({ artifactSlug }) => ({
          artifactSlug,
          keepFromFileVersion: fileVersion,
        })),
      });
      onClose();
      onRunAgain({ fileVersion: submission.fileVersion, started: true });
    } catch (error) {
      setErrorMessage(commonError.getErrorMessage(error));
    } finally {
      setStarting(false);
    }
  };

  return (
    <>
      <Button
        variant="link"
        size="sm"
        fontWeight="normal"
        alignSelf="flex-start"
        onClick={onOpen}
      >
        Run again…
      </Button>
      <UploadModal
        isOpen={isOpen}
        onClose={onClose}
        modalHeaderText={`Run again as v${nextFileVersion}`}
      >
        <VStack alignItems="flex-start" spacing="12px">
          <Text>
            {`Keep these files from v${fileVersion}. Unchecked files are left for you to upload in v${nextFileVersion}.`}
          </Text>
          {keepable.map(({ artifactSlug, name }) => (
            <Checkbox
              key={artifactSlug}
              isChecked={!unchecked.has(artifactSlug)}
              onChange={(event) => {
                const next = new Set(unchecked);
                if (event.target.checked) {
                  next.delete(artifactSlug);
                } else {
                  next.add(artifactSlug);
                }
                setUnchecked(next);
              }}
            >
              {name}
            </Checkbox>
          ))}
          {errorMessage && <Text color="red.500">{errorMessage}</Text>}
          <Button alignSelf="flex-end" isLoading={starting} onClick={start}>
            {kept.length
              ? `Start v${nextFileVersion}`
              : `Upload into v${nextFileVersion}`}
          </Button>
        </VStack>
      </UploadModal>
    </>
  );
};
